import {useEffect, useRef} from 'react';

import {
  getMainViewItemFromUrlParams,
  getUrlParamsFromSelectedItem,
  parseAnalyticsUrlQueryParameters,
} from 'analysis-tool/AnalysisToolUrls';
import {getViewForViewReference} from 'analysis-tool/data-view/AnalysisViewUtils';
import {selectedItemGlobalState} from 'analysis-tool/ui/hooks/selectedItemGlobalState';
import {AnalysisMainViewItem, AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import {setURLParams} from 'BenchlingUtil';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import {dataFrameTableModeState} from 'data-frames/table/hooks/useDataFrameTableMode';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getOutputDataForView, isDataFrameView} from 'pipelines/PipelineViewUtils';

const NOOP_TABLE_MODE_CLIENT_ID = 'noop';

/**
 * Synchronizes the browser URL with the selected analysis main view item.
 *
 * On mount: if no item is selected in global state, reads URL params and seeds initial state.
 * On item change: updates URL query params for linkable states (FLOWCHART, DATA_VIEW, and
 * step-aware output screens like SAVE_TO_RESULTS_OUTPUT, SEND_TO_DOCUMENT, REGRESSION).
 * Transient states (RUNNING, FAILED, etc.) do not update the URL.
 *
 * Loop prevention: URL is only parsed once on mount (when global state is null). After that, state is
 * the source of truth. history.replaceState does not trigger Backbone routing.
 *
 * @package
 */
export function useAnalysisDeepLinkSync(
  clientStateId: string,
  analysis: PipelineGraphQL,
  selectedItem: AnalysisMainViewItem
): void {
  const hasInitializedRef = useRef(false);

  // URL → State: on first render, if global state is null, seed from URL params
  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    const existingState = selectedItemGlobalState.getState(clientStateId);
    if (existingState != null) {
      return;
    }

    const urlParams = parseAnalyticsUrlQueryParameters();
    const itemFromUrl = getMainViewItemFromUrlParams(urlParams, analysis);
    if (itemFromUrl != null) {
      selectedItemGlobalState.updateState(clientStateId, itemFromUrl);
      // Seed table mode state so rawTableMode reflects showChips from the URL immediately,
      // before DataFrameView mounts and the toggle-action writes to dataFrameTableModeState.
      if (itemFromUrl.type === AnalysisMainViewItemType.DATA_VIEW && itemFromUrl.showChips) {
        const seedView = getViewForViewReference(analysis, itemFromUrl.viewReference);
        if (seedView != null && isDataFrameView(seedView, analysis.steps)) {
          const seedDataFrame = getOutputDataForView(seedView, analysis.steps, PipelineStepDataKind.DATASET);
          if (seedDataFrame != null) {
            dataFrameTableModeState.updateState(seedDataFrame.id, DataFrameTableMode.RICH);
          }
        }
      }
    }
  }, [clientStateId, analysis]);

  // Subscribe to the table mode for the current DATA_VIEW so URL stays in sync when the
  // user toggles chips. For non-DATA_VIEW items (or when the view isn't found), key off
  // a dummy id so the hook call is unconditional (rules of hooks).
  const view =
    selectedItem.type === AnalysisMainViewItemType.DATA_VIEW
      ? getViewForViewReference(analysis, selectedItem.viewReference)
      : null;
  // DataFrameView keys dataFrameTableModeState by the persisted data frame's ID (the raw data ID),
  // not by getDefaultClientIdForView. Match that key here so the subscription fires on toggle.
  const dataFrameRawData =
    view != null && isDataFrameView(view, analysis.steps)
      ? getOutputDataForView(view, analysis.steps, PipelineStepDataKind.DATASET)
      : null;
  const tableModeClientId = dataFrameRawData != null ? dataFrameRawData.id : NOOP_TABLE_MODE_CLIENT_ID;
  const [rawTableMode] = dataFrameTableModeState.useGlobalClientState(tableModeClientId);

  // State → URL: update URL when selected item or table mode changes
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    // Skip the first effect run to avoid overwriting URL on initial load
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    const params = getUrlParamsFromSelectedItem(selectedItem);
    // Derive showChips from the actual table mode state (runtime source of truth).
    // selectedItem.showChips is only a URL→state seed on initial load.
    const showChips = rawTableMode === DataFrameTableMode.RICH ? true : null;
    // Update URL for both linkable states (non-null screen) and non-linkable states
    // (null screen) to clear stale deep-link params from the address bar.
    const newUrl = setURLParams(window.location.href, {...params, showChips});
    // Only update the URL if it actually changed to avoid unnecessary replaceState calls
    // and potential re-encoding issues with special characters in query params.
    const currentUrl = window.location.pathname + window.location.search;
    if (newUrl !== currentUrl) {
      window.history.replaceState(window.history.state, '', newUrl);
    }
  }, [selectedItem, rawTableMode]);
}
